import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Circle, Upload, Users, CalendarClock, FolderOpen, MailSearch, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { AttentionPanels } from "@/components/dashboard/AttentionPanels";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatDate } from "@/lib/api";

const Kpi = ({ label, value, icon: Icon, tone, testId, delay, to, hint }) => (
  <Link to={to} className={`panel block p-5 transition-transform hover:-translate-y-0.5 animate-rise ${delay}`} data-testid={testId}>
    <div className="flex items-center justify-between"><p className="eyebrow">{label}</p><Icon className={`h-4 w-4 ${tone}`} /></div>
    <p className="mt-4 font-display text-4xl font-extrabold tracking-tight text-slate-900">{value ?? "—"}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </Link>
);

const Setup = ({ data, isAdmin }) => {
  const steps = [
    { done: data.employees_active > 0, label: "Aggiungi i dipendenti", to: "/dipendenti" },
    { done: data.imap_configured, label: "Collega la casella email per le importazioni", to: "/impostazioni" },
    { done: data.smtp_configured, label: "Configura la casella per l'invio dei cedolini (SMTP)", to: "/impostazioni" },
    { done: data.pdf_password_set, label: "Salva la password del PDF dei cedolini", to: "/impostazioni" },
  ];
  if (!isAdmin || steps.every((s) => s.done)) return null;
  return (
    <div className="panel p-6 animate-rise" data-testid="setup-checklist">
      <p className="eyebrow">Primi passi</p>
      <ul className="mt-4 space-y-3">
        {steps.map((s) => (
          <li key={s.label} className="flex items-center gap-3 text-sm">
            {s.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
            <Link to={s.to} className={s.done ? "text-slate-400 line-through" : "text-slate-800 hover:text-[#3d6e49]"}>{s.label}</Link>
          </li>))}
      </ul>
    </div>
  );
};

const LastCheck = ({ data }) => {
  const lc = data.last_check;
  return (
    <div className="panel p-6" data-testid="last-check-card">
      <div className="flex items-center justify-between"><p className="eyebrow">Ultimo controllo email</p><Link to="/importazioni" className="text-xs font-medium text-[#3d6e49] hover:underline" data-testid="view-imports-link">Apri importazioni</Link></div>
      {lc ? (
        <div className="mt-4 flex items-start gap-3 text-sm">
          {lc.status === "running" ? <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-slate-500" /> : lc.status === "error" ? <AlertTriangle className="mt-0.5 h-4 w-4 text-rose-600" /> : <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />}
          <div><p className="font-medium text-slate-900">{lc.status === "running" ? "In corso" : lc.status === "error" ? "Interrotto con errori" : "Completato"} · {formatDate(lc.at)}</p>
            <p className="text-xs text-slate-500">{lc.imported} documenti importati{lc.errors ? ` · ${lc.errors} errori` : ""} · casella {data.imap_account || "non collegata"}</p></div>
        </div>
      ) : <p className="mt-4 text-sm text-slate-500">Nessun controllo effettuato. {data.imap_configured ? "Premi \"Controlla ora\" in Importazioni email." : "Collega la casella in Impostazioni."}</p>}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  return (
    <>
      <PageHeader eyebrow="Panoramica" title="Buongiorno." description="Lo stato del personale: chi è in forza, cosa scade, cosa manca e cosa c'è da verificare."
        actions={<Button asChild className="active:scale-[0.98]" data-testid="dashboard-upload-button"><Link to="/cedolini"><Upload className="mr-2 h-4 w-4" /> Carica cedolini</Link></Button>} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Dipendenti attivi" value={data?.employees_active} icon={Users} tone="text-slate-500" testId="kpi-employees-active" delay="delay-1" to="/dipendenti" />
        <Kpi label="Contratti in scadenza" value={data?.deadlines_count} icon={CalendarClock} tone="text-amber-500" testId="kpi-deadlines" delay="delay-2" to="/scadenze" hint="entro l'anticipo configurato" />
        <Kpi label="Documenti mancanti" value={data?.documents_missing} icon={FolderOpen} tone="text-rose-500" testId="kpi-documents-missing" delay="delay-3" to="/documenti" hint="attivi senza alcun documento" />
        <Kpi label="Da verificare" value={data?.review_pending} icon={MailSearch} tone="text-[#7fb58a]" testId="kpi-review-pending" delay="delay-4" to="/importazioni" hint="importazioni dubbie" />
      </div>
      {data && <Setup data={data} isAdmin={user?.role === "admin"} />}
      {data && <AttentionPanels data={data} />}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-5">
          {data && <LastCheck data={data} />}
          <div className="panel p-6" data-testid="last-batch-card">
            <p className="eyebrow">Ultimo caricamento cedolini</p>
            {data?.last_batch ? (
              <div className="mt-4 space-y-4">
                <div><p className="font-display text-xl font-bold text-slate-900">{data.last_batch.period || "Periodo non rilevato"}</p><p className="truncate text-sm text-slate-500">{data.last_batch.filename} · {formatDate(data.last_batch.created_at)}</p></div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  {[["Cedolini", data.last_batch.summary.total], ["Inviati", data.last_batch.summary.sent], ["Da verificare", data.last_batch.summary.issues]].map(([l, v]) => (
                    <div key={l} className="rounded-md bg-slate-50 py-3"><p className="font-display text-2xl font-bold text-slate-900">{v}</p><p className="text-xs text-slate-500">{l}</p></div>))}
                </div>
                <Button asChild variant="outline" className="w-full" data-testid="open-last-batch-button"><Link to={`/cedolini/${data.last_batch.id}`}>Apri <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              </div>
            ) : <p className="mt-4 text-sm text-slate-500">Nessun PDF caricato finora.</p>}
          </div>
        </div>
        <div className="panel p-6 lg:col-span-7" data-testid="recent-dispatches">
          <div className="flex items-center justify-between"><p className="eyebrow">Ultimi invii cedolini</p><Link to="/cedolini/storico" className="text-xs font-medium text-[#3d6e49] hover:underline" data-testid="view-history-link">Vedi tutti</Link></div>
          <ul className="mt-4 divide-y divide-slate-100">
            {data?.recent?.length ? data.recent.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-slate-900">{d.employee_name}</p><p className="truncate text-xs text-slate-500">{d.email} · {d.period || "—"} · {formatDate(d.sent_at)}</p></div>
                <StatusBadge status={d.status} />
              </li>)) : <li className="py-3 text-sm text-slate-500">Ancora nessun invio.</li>}
          </ul>
        </div>
      </div>
    </>
  );
}
